/* eslint-disable jsx-a11y/no-autofocus */
import React, {useRef, useState} from 'react';
import {Button} from '../../../components';
import styles from './CodeInput.module.scss';
import {usePolyglot} from '../../../context/Polyglot';

interface CodeInputProps {
    submit: (code: string) => void;
    success: boolean;
    loading: boolean;
    buttonText: string | JSX.Element;
    buttonError?: string;
}

export const BasicCodeInput = ({
    codeInputs,
    setCodeInputs,
    activeInput,
    setActiveInput,
    submit,
    loading,
    inputsRefs,
    code = '',
	numbersOnly = false,
	disabled
}: {
    codeInputs: string[];
    setCodeInputs: (vals: string[]) => void;
    activeInput: number | undefined;
    setActiveInput: React.Dispatch<React.SetStateAction<number | undefined>>;
    submit?: (submitCode: string) => void;
    loading: boolean;
    inputsRefs: React.MutableRefObject<HTMLInputElement[]>;
    code?: string;
	numbersOnly?: boolean;
	disabled?: boolean;
}) => (
    <div className={styles.input} data-is-disabled={(disabled || false).toString()}>
        {codeInputs.map((value, index) => (
            <div
                key={index}
                className={[
                    styles.inputContainer,
                    activeInput === index ? styles.active : undefined,
                    value ? styles.filled : undefined,
                    loading ? styles.disabled : undefined
                ].join(' ')}
            >
                <p
                    className={[
                        styles.number,
                        value ? styles.numberEntrance : styles.numberExit
                    ].join(' ')}
                >
                    {value}
                </p>
                <input
	                disabled={disabled}
                    ref={element => {
                        if (element) {
	                        // eslint-disable-next-line no-param-reassign
                            inputsRefs.current[index] = element;
                        }
                    }}
                    style={{ opacity: value ? 0 : 1 }}
                    type='text'
                    value={value}
                    onFocus={() => setActiveInput(index)}
                    onBlur={() => index === activeInput && setActiveInput(undefined)}
                    onChange={({ target }) => {
						if (numbersOnly) {
							const isNumber = /^[0-9]$/.test(target.value.slice(-1));
							const isEmpty = target.value === '';
							if (!isNumber && !isEmpty) return;
						}
                        setCodeInputs(
                            codeInputs.map((val, codeIndex) =>
                                codeIndex === index ? target.value.slice(-1) : val
                            )
                        );

                        if (target.value.length >= 1) {
                            inputsRefs.current?.[index + 1]?.focus();
                        }
                    }}
                    onKeyDown={event => {
                        if (
                            event.key === 'Backspace' &&
                            codeInputs[index] === '' &&
                            index - 1 > -1
                        ) {
                            inputsRefs.current?.[index - 1]?.focus();
                        }
                    }}
                    onKeyUp={() => {
                        if (code.length === 6) {
                            submit?.(code);
                        }
                    }}
                    autoFocus={index === 0}
                    onPaste={event => {
                        const pastedCode = event.clipboardData.getData('text').replace(/ /g, '');

                        if (pastedCode && pastedCode.length === 6 && /^-?\d+$/.test(pastedCode)) {
                            setCodeInputs(pastedCode.split(''));
                        }
                    }}
                />
                <div className={styles.cursor} />
            </div>
        ))}
    </div>
);

export const CodeInput: React.FC<CodeInputProps> = ({
    submit,
    success,
    loading,
    buttonText,
    buttonError
}) => {
    const [codeInputs, setCodeInputs] = useState<string[]>(['', '', '', '', '', '']);
    const [activeInput, setActiveInput] = useState<number>();

    const inputsRefs = useRef<HTMLInputElement[]>([]);

    const { t } = usePolyglot();

    const code = codeInputs.join('');
    let text: string | JSX.Element =
        code.length === 6 ? buttonText : t('phrases.%count_digits_left', 6 - code.length);

    if (success) {
        text = <i className='icon-check' />;
    }

    if (buttonError) {
        text = buttonError;
    }

    return (
        <div
            className={[
                styles.codeInput,
                success ? styles.success : '',
                buttonError ? styles.error : ''
            ]
                .join(' ')
                .trim()}
        >
            <BasicCodeInput
                codeInputs={codeInputs}
                setCodeInputs={setCodeInputs}
                activeInput={activeInput}
                setActiveInput={setActiveInput}
                submit={submit}
                loading={loading}
                inputsRefs={inputsRefs}
                code={code}
            />
            <Button onClick={() => submit(code)} loading={loading} disabled={code.length !== 6}>
                {text}
            </Button>
        </div>
    );
};

export default CodeInput;
